import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  Cog6ToothIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { FriendSummary, UpdateFriendshipLimits } from '../api/friends';

interface FriendLimitSettingsModalProps {
  friend: FriendSummary | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (friendshipId: string, limits: UpdateFriendshipLimits) => Promise<void>;
}

const FriendLimitSettingsModal: React.FC<FriendLimitSettingsModalProps> = ({
  friend,
  isOpen,
  onClose,
  onUpdate,
}) => {
  const [autoApprove, setAutoApprove] = useState(false);
  const [bandwidthLimit, setBandwidthLimit] = useState<string>('');
  const [dataLimit, setDataLimit] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Initialize form values when friend changes
  useEffect(() => {
    if (friend) {
      setAutoApprove(friend.auto_approve_connections);
      setBandwidthLimit(friend.bandwidth_limit_mbps ? String(friend.bandwidth_limit_mbps) : '');
      setDataLimit(friend.daily_data_limit_gb ? String(friend.daily_data_limit_gb) : '');
    }
  }, [friend]);

  if (!isOpen || !friend) return null;

  const handleUpdate = async () => {
    setIsProcessing(true);
    try {
      const limits: UpdateFriendshipLimits = {
        auto_approve_connections: autoApprove,
        bandwidth_limit_mbps: bandwidthLimit ? parseInt(bandwidthLimit) : null,
        daily_data_limit_gb: dataLimit ? parseInt(dataLimit) : null,
      };

      await onUpdate(friend.friendship_id, limits);
      handleClose();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              onClick={handleClose}
              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              <div className="flex items-center mb-4">
                <Cog6ToothIcon className="h-6 w-6 text-gray-400 mr-3" />
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Connection Limit Settings
                </h3>
              </div>

              {/* Friend Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-base font-bold text-primary-600">
                        {friend.friend.display_name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">
                      {friend.friend.display_name}
                    </div>
                    {friend.friend.username && (
                      <div className="text-sm text-gray-500">
                        @{friend.friend.username}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Info Notice */}
              <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      These settings control how {friend.friend.display_name} can connect through your device.
                      Leave fields empty for unlimited access.
                    </p>
                  </div>
                </div>
              </div>

              {/* Limit Controls */}
              <div className="space-y-4 mb-4">
                {/* Auto-approve checkbox */}
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="auto-approve"
                      type="checkbox"
                      checked={autoApprove}
                      onChange={(e) => setAutoApprove(e.target.checked)}
                      className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="auto-approve" className="font-medium text-gray-700">
                      Auto-approve connection requests
                    </label>
                    <p className="text-gray-500">
                      Automatically accept when they request to connect
                    </p>
                  </div>
                </div>

                {/* Bandwidth Limit */}
                <div>
                  <label htmlFor="bandwidth-limit" className="block text-sm font-medium text-gray-700 mb-1">
                    Bandwidth Limit (Mbps)
                  </label>
                  <input
                    id="bandwidth-limit"
                    type="number"
                    min="1"
                    value={bandwidthLimit}
                    onChange={(e) => setBandwidthLimit(e.target.value)}
                    placeholder="Unlimited"
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Maximum speed they can use (empty = unlimited)
                  </p>
                </div>

                {/* Data Limit */}
                <div>
                  <label htmlFor="data-limit" className="block text-sm font-medium text-gray-700 mb-1">
                    Daily Data Limit (GB)
                  </label>
                  <input
                    id="data-limit"
                    type="number"
                    min="1"
                    value={dataLimit}
                    onChange={(e) => setDataLimit(e.target.value)}
                    placeholder="Unlimited"
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Maximum data per day (empty = unlimited)
                  </p>
                </div>

                {/* Current Status */}
                {(friend.bandwidth_limit_mbps || friend.daily_data_limit_gb) && (
                  <div className="bg-gray-50 rounded-md p-3 border border-gray-200">
                    <p className="text-xs font-medium text-gray-700 mb-1">Current Limits:</p>
                    <ul className="text-xs text-gray-600 space-y-1">
                      {friend.bandwidth_limit_mbps && (
                        <li>Bandwidth: {friend.bandwidth_limit_mbps} Mbps</li>
                      )}
                      {friend.daily_data_limit_gb && (
                        <li>Daily Data: {friend.daily_data_limit_gb} GB</li>
                      )}
                      {!friend.bandwidth_limit_mbps && !friend.daily_data_limit_gb && (
                        <li>No limits currently set</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse space-y-3 sm:space-y-0 sm:space-x-3 sm:space-x-reverse">
                <button
                  type="button"
                  disabled={isProcessing}
                  onClick={handleUpdate}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {isProcessing ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  disabled={isProcessing}
                  onClick={handleClose}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FriendLimitSettingsModal;
